// Local storage utilities for managing video downloads
// This provides offline-first functionality while also syncing to Redis when online

import { useState, useEffect } from 'react';

export interface DownloadRecord {
  videoId: string;
  quality: string;
  fileUrl: string;
  fileName: string;
  title: string;
  author: string;
  thumbnail: string;
  fileSize?: number;
  downloadedAt: number;
  lastWatched?: number;
  watchCount: number;
}

export interface DownloadProgress {
  videoId: string;
  status: 'pending' | 'downloading' | 'processing' | 'completed' | 'failed';
  progress: number;
  quality?: string;
  fileUrl?: string;
  error?: string;
  timestamp: number;
}

class DownloadStorageManager {
  private readonly STORAGE_KEY = 'video_downloads';
  private readonly PROGRESS_KEY = 'download_progress';
  private readonly MAX_DOWNLOADS = 50;

  // Get all downloaded videos
  getDownloads(): DownloadRecord[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error reading downloads from localStorage:', error);
      return [];
    }
  }

  // Add new download record
  addDownload(record: DownloadRecord): void {
    try {
      const downloads = this.getDownloads();
      
      // Remove existing record for the same video if exists
      const filteredDownloads = downloads.filter(d => d.videoId !== record.videoId);
      
      // Add new record at the beginning
      filteredDownloads.unshift(record);
      
      // Keep only the latest MAX_DOWNLOADS
      const trimmedDownloads = filteredDownloads.slice(0, this.MAX_DOWNLOADS);
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(trimmedDownloads));
      
      // Sync to Redis if possible
      this.syncToRedis(record);
    } catch (error) {
      console.error('Error saving download to localStorage:', error);
    }
  }

  // Get specific download by video ID
  getDownload(videoId: string): DownloadRecord | null {
    const downloads = this.getDownloads();
    return downloads.find(d => d.videoId === videoId) || null;
  }

  // Update download record (e.g., increment watch count)
  updateDownload(videoId: string, updates: Partial<DownloadRecord>): void {
    try {
      const downloads = this.getDownloads();
      const index = downloads.findIndex(d => d.videoId === videoId);
      
      if (index !== -1) {
        downloads[index] = { ...downloads[index], ...updates };
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(downloads));
        
        // Sync update to Redis
        this.syncToRedis(downloads[index]);
      }
    } catch (error) {
      console.error('Error updating download:', error);
    }
  }

  // Remove download record
  removeDownload(videoId: string): void {
    try {
      const downloads = this.getDownloads();
      const filteredDownloads = downloads.filter(d => d.videoId !== videoId);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredDownloads));
    } catch (error) {
      console.error('Error removing download:', error);
    }
  }

  // Mark video as watched
  markWatched(videoId: string): void {
    const download = this.getDownload(videoId);
    if (download) {
      this.updateDownload(videoId, {
        lastWatched: Date.now(),
        watchCount: download.watchCount + 1
      });
    }
  }

  // Download progress management
  saveProgress(progress: DownloadProgress): void {
    try {
      const progressData = localStorage.getItem(this.PROGRESS_KEY);
      const allProgress = progressData ? JSON.parse(progressData) : {};
      
      allProgress[progress.videoId] = progress;
      
      localStorage.setItem(this.PROGRESS_KEY, JSON.stringify(allProgress));
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  }

  getProgress(videoId: string): DownloadProgress | null {
    try {
      const progressData = localStorage.getItem(this.PROGRESS_KEY);
      const allProgress = progressData ? JSON.parse(progressData) : {};
      return allProgress[videoId] || null;
    } catch (error) {
      console.error('Error reading progress:', error);
      return null;
    }
  }

  clearProgress(videoId: string): void {
    try {
      const progressData = localStorage.getItem(this.PROGRESS_KEY);
      const allProgress = progressData ? JSON.parse(progressData) : {};
      delete allProgress[videoId];
      localStorage.setItem(this.PROGRESS_KEY, JSON.stringify(allProgress));
    } catch (error) {
      console.error('Error clearing progress:', error);
    }
  }

  // Get storage usage statistics
  getStorageStats(): {
    totalDownloads: number;
    totalSize: number;
    oldestDownload: number | null;
    newestDownload: number | null;
  } {
    const downloads = this.getDownloads();
    
    return {
      totalDownloads: downloads.length,
      totalSize: downloads.reduce((sum, d) => sum + (d.fileSize || 0), 0),
      oldestDownload: downloads.length > 0 ? Math.min(...downloads.map(d => d.downloadedAt)) : null,
      newestDownload: downloads.length > 0 ? Math.max(...downloads.map(d => d.downloadedAt)) : null,
    };
  }

  // Clean up old downloads based on age or count
  cleanup(maxAge?: number, maxCount?: number): void {
    try {
      let downloads = this.getDownloads();
      
      // Remove downloads older than maxAge (in milliseconds)
      if (maxAge) {
        const cutoff = Date.now() - maxAge;
        downloads = downloads.filter(d => d.downloadedAt > cutoff);
      }
      
      // Keep only maxCount newest downloads
      if (maxCount && downloads.length > maxCount) {
        downloads = downloads
          .sort((a, b) => b.downloadedAt - a.downloadedAt)
          .slice(0, maxCount);
      }
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(downloads));
    } catch (error) {
      console.error('Error during cleanup:', error);
    }
  }

  // Sync to Redis when online (fire and forget)
  private async syncToRedis(record: DownloadRecord): Promise<void> {
    try {
      // Only attempt if we have a user ID
      const userId = localStorage.getItem('userId');
      if (!userId) return;

      await fetch('/api/user/downloads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          ...record
        }),
      });
    } catch (error) {
      // Silent fail - local storage is the primary source
      console.log('Could not sync to Redis:', error);
    }
  }

  // Import downloads from Redis
  async importFromRedis(userId: string): Promise<void> {
    try {
      const response = await fetch(`/api/user/downloads?userId=${userId}`);
      if (response.ok) {
        const remoteDownloads: DownloadRecord[] = await response.json();
        const localDownloads = this.getDownloads();
        
        // Merge with local downloads, preferring local data for conflicts
        const mergedDownloads = [...localDownloads];
        
        remoteDownloads.forEach(remote => {
          const existsLocally = localDownloads.some(local => local.videoId === remote.videoId);
          if (!existsLocally) {
            mergedDownloads.push(remote);
          }
        });
        
        // Sort by download date and limit
        const sortedDownloads = mergedDownloads
          .sort((a, b) => b.downloadedAt - a.downloadedAt)
          .slice(0, this.MAX_DOWNLOADS);
        
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(sortedDownloads));
      }
    } catch (error) {
      console.error('Error importing from Redis:', error);
    }
  }
}

// Export singleton instance
export const downloadStorage = new DownloadStorageManager();

// Hook for React components
export function useDownloadStorage() {
  const [downloads, setDownloads] = useState<DownloadRecord[]>([]);

  const refreshDownloads = () => {
    setDownloads(downloadStorage.getDownloads());
  };

  useEffect(() => {
    refreshDownloads();
    
    // Listen for storage changes
    const handleStorageChange = () => {
      refreshDownloads();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return {
    downloads,
    refreshDownloads,
    addDownload: (record: DownloadRecord) => {
      downloadStorage.addDownload(record);
      refreshDownloads();
    },
    removeDownload: (videoId: string) => {
      downloadStorage.removeDownload(videoId);
      refreshDownloads();
    },
    markWatched: (videoId: string) => {
      downloadStorage.markWatched(videoId);
      refreshDownloads();
    },
    getStorageStats: downloadStorage.getStorageStats.bind(downloadStorage),
    cleanup: (maxAge?: number, maxCount?: number) => {
      downloadStorage.cleanup(maxAge, maxCount);
      refreshDownloads();
    }
  };
}